// Retro Pacman-style audio generator using Web Audio API

class PacmanAudio {
  constructor() {
    this.audioContext = null;
    this.bgMusicOscillator = null;
    this.bgMusicGain = null;
    this.isPlaying = false;
    this.isMuted = true; // Default to muted
    this.volume = 0.5; // Default volume (50%)
    
    // Initialize audio context on first user interaction
    this.initAudioContext = this.initAudioContext.bind(this);
  }

  // Initialize audio context (must be called after user interaction)
  initAudioContext() {
    // If context exists but is closed, reset it
    if (this.audioContext && this.audioContext.state === 'closed') {
      console.log('Audio context was closed, resetting...');
      this.audioContext = null;
    }
    
    if (!this.audioContext) {
      try {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (!AudioContext) {
          console.error('Web Audio API not supported in this browser');
          return null;
        }
        this.audioContext = new AudioContext();
        console.log('Audio context initialized, state:', this.audioContext.state);
      } catch (e) {
        console.error('Failed to create audio context:', e);
        return null;
      }
    }
    return this.audioContext;
  }

  // Get audio context (for external access)
  getAudioContext() {
    return this.audioContext;
  }

  // Ensure audio context is ready (initialize and resume if needed)
  async ensureAudioReady() {
    try {
      // If audio context is closed, we need to create a new one
      if (!this.audioContext || this.audioContext.state === 'closed') {
        console.log('Audio context is closed or missing, creating new one...');
        this.audioContext = null; // Reset to allow recreation
        this.initAudioContext();
        if (!this.audioContext) {
          console.error('Failed to create new audio context');
          return false;
        }
        console.log('New audio context created, state:', this.audioContext.state);
      }
      
      if (this.audioContext.state === 'suspended') {
        console.log('Resuming suspended audio context...');
        await this.audioContext.resume();
        console.log('Audio context resumed, state:', this.audioContext.state);
      }
      
      const isReady = this.audioContext.state === 'running';
      console.log('Audio ready check:', isReady, 'state:', this.audioContext.state);
      return isReady;
    } catch (e) {
      console.error('Error ensuring audio ready:', e);
      // Try to recreate audio context on error
      this.audioContext = null;
      return false;
    }
  }

  // Test audio function - plays a simple beep
  async testAudio() {
    console.log('=== TESTING AUDIO ===');
    console.log('Audio context exists:', !!this.audioContext);
    console.log('Audio context state:', this.audioContext?.state);
    console.log('Is muted:', this.isMuted);
    
    // Force initialization if needed
    if (!this.audioContext) {
      console.log('Initializing audio context...');
      this.initAudioContext();
    }
    
    if (!this.audioContext) {
      console.error('Failed to create audio context');
      alert('Web Audio API not supported or failed to initialize. Check console for details.');
      return false;
    }
    
    const ready = await this.ensureAudioReady();
    console.log('Audio ready after ensure:', ready);
    console.log('Final audio context state:', this.audioContext.state);
    
    if (!ready || this.audioContext.state !== 'running') {
      console.error('Audio context not ready for test. State:', this.audioContext.state);
      alert(`Audio not ready. State: ${this.audioContext.state}. Check console for details.`);
      return false;
    }

    try {
      console.log('Creating oscillator and gain nodes...');
      const osc = this.audioContext.createOscillator();
      const gain = this.audioContext.createGain();
      
      osc.type = 'square';
      osc.frequency.value = 440;
      gain.gain.value = 0.8; // Higher volume for testing
      
      console.log('Connecting audio nodes...');
      osc.connect(gain);
      gain.connect(this.audioContext.destination);
      
      const now = this.audioContext.currentTime;
      console.log('Current audio time:', now);
      
      gain.gain.setValueAtTime(0.8, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.3);
      
      console.log('Starting oscillator...');
      osc.start(now);
      osc.stop(now + 0.3);
      
      console.log('Test sound should be playing now!');
      console.log('=== TEST COMPLETE ===');
      
      // Return true after a short delay to ensure sound plays
      return new Promise((resolve) => {
        setTimeout(() => {
          console.log('Test completed');
          resolve(true);
        }, 350);
      });
    } catch (e) {
      console.error('Error playing test sound:', e);
      alert(`Error playing test sound: ${e.message}. Check console for details.`);
      return false;
    }
  }

  // Create a retro chiptune-style oscillator
  createOscillator(frequency, type = 'square', detune = 0) {
    if (!this.audioContext) {
      this.initAudioContext();
    }
    const osc = this.audioContext.createOscillator();
    osc.type = type;
    osc.frequency.value = frequency;
    osc.detune.value = detune;
    return osc;
  }

  // Create gain node for volume control
  createGainNode(volume = 1.0) {
    if (!this.audioContext) {
      this.initAudioContext();
    }
    const gain = this.audioContext.createGain();
    gain.gain.value = volume;
    return gain;
  }

  // Play background music (Pacman-style looping theme)
  async playBackgroundMusic() {
    if (this.isPlaying || this.isMuted) return;
    
    // Ensure audio is ready before playing
    const ready = await this.ensureAudioReady();
    if (!ready) {
      console.warn('Audio context not ready for background music');
      return;
    }

    this.stopBackgroundMusic(); // Stop any existing music

    // Create gain node for background music
    this.bgMusicGain = this.audioContext.createGain();
    this.bgMusicGain.gain.value = this.volume * 0.6; // Background music at 60% of master volume
    this.bgMusicGain.connect(this.audioContext.destination);

    // Classic Pacman intermission-style melody
    // This is a simplified version of the classic Pacman intermission tune
    const notes = [
      { freq: 523.25, duration: 0.15 }, // C5
      { freq: 659.25, duration: 0.15 }, // E5
      { freq: 784.00, duration: 0.15 }, // G5
      { freq: 659.25, duration: 0.15 }, // E5
      { freq: 523.25, duration: 0.15 }, // C5
      { freq: 440.00, duration: 0.15 }, // A4
      { freq: 523.25, duration: 0.15 }, // C5
      { freq: 659.25, duration: 0.15 }, // E5
      { freq: 784.00, duration: 0.15 }, // G5
      { freq: 880.00, duration: 0.15 }, // A5
      { freq: 784.00, duration: 0.15 }, // G5
      { freq: 659.25, duration: 0.15 }, // E5
      { freq: 523.25, duration: 0.3 },  // C5
      { freq: 392.00, duration: 0.15 }, // G4
      { freq: 440.00, duration: 0.15 }, // A4
      { freq: 523.25, duration: 0.3 },  // C5
    ];

    const loopDuration = notes.reduce((sum, note) => sum + note.duration, 0);

    const playLoop = () => {
      if (!this.isPlaying || this.isMuted || !this.bgMusicGain) return;
      
      const currentTime = this.audioContext.currentTime;
      
      notes.forEach((note, index) => {
        const osc = this.createOscillator(note.freq, 'square');
        const gain = this.createGainNode(0.5);
        
        osc.connect(gain);
        gain.connect(this.bgMusicGain);
        
        const noteStart = currentTime + (notes.slice(0, index).reduce((sum, n) => sum + n.duration, 0));
        
        gain.gain.setValueAtTime(0.5, noteStart);
        gain.gain.exponentialRampToValueAtTime(0.01, noteStart + note.duration);
        
        osc.start(noteStart);
        osc.stop(noteStart + note.duration);
      });

      // Schedule next loop
      setTimeout(() => {
        if (this.isPlaying && !this.isMuted && this.bgMusicGain) {
          playLoop();
        }
      }, loopDuration * 1000);
    };

    this.isPlaying = true;
    playLoop();
  }

  // Stop background music
  stopBackgroundMusic() {
    this.isPlaying = false;
    if (this.bgMusicGain) {
      this.bgMusicGain.disconnect();
      this.bgMusicGain = null;
    }
  }

  // Play dot eating sound (wakka-wakka)
  async playDotEat() {
    if (this.isMuted) return;
    
    // Ensure audio is ready
    const ready = await this.ensureAudioReady();
    if (!ready) {
      console.log('Audio not ready for dot eat sound');
      return;
    }

    try {
      const osc = this.createOscillator(440, 'square');
      const gain = this.createGainNode(0.3); // Higher volume for testing
      
      osc.connect(gain);
      gain.connect(this.audioContext.destination);
      
      const now = this.audioContext.currentTime;
      gain.gain.setValueAtTime(0.3, now);
      gain.gain.exponentialRampToValueAtTime(0.01, now + 0.1);
      
      osc.start(now);
      osc.stop(now + 0.1);
      
      console.log('Dot eat sound played');
    } catch (e) {
      console.error('Error playing dot eat sound:', e);
    }
  }

  // Play power pellet collection sound
  async playPowerPellet() {
    if (this.isMuted) return;
    
    // Ensure audio is ready
    const ready = await this.ensureAudioReady();
    if (!ready) return;

    // Rising tone effect
    const osc1 = this.createOscillator(220, 'square');
    const osc2 = this.createOscillator(330, 'square');
    const gain = this.createGainNode(this.volume * 0.8);
    
    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(this.audioContext.destination);
    
    const startTime = this.audioContext.currentTime;
    
    osc1.frequency.linearRampToValueAtTime(440, startTime + 0.2);
    osc2.frequency.linearRampToValueAtTime(660, startTime + 0.2);
    
    gain.gain.setValueAtTime(this.volume * 0.8, startTime);
    gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.2);
    
    osc1.start(startTime);
    osc2.start(startTime);
    osc1.stop(startTime + 0.2);
    osc2.stop(startTime + 0.2);
  }

  // Play ghost eating sound
  async playGhostEat() {
    if (this.isMuted) return;
    
    // Ensure audio is ready
    const ready = await this.ensureAudioReady();
    if (!ready) return;

    // Quick ascending notes
    const frequencies = [523.25, 659.25, 784.00, 1046.50]; // C5, E5, G5, C6
    
    frequencies.forEach((freq, index) => {
      const osc = this.createOscillator(freq, 'square');
      const gain = this.createGainNode(this.volume * 0.8);
      
      osc.connect(gain);
      gain.connect(this.audioContext.destination);
      
      const startTime = this.audioContext.currentTime + (index * 0.05);
      
      gain.gain.setValueAtTime(this.volume * 0.8, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.05);
      
      osc.start(startTime);
      osc.stop(startTime + 0.05);
    });
  }

  // Play game over sound
  async playGameOver() {
    if (this.isMuted) return;
    
    // Ensure audio is ready
    const ready = await this.ensureAudioReady();
    if (!ready) return;

    // Descending sad melody
    const frequencies = [523.25, 493.88, 440.00, 392.00, 349.23]; // C5, B4, A4, G4, F4
    
    frequencies.forEach((freq, index) => {
      const osc = this.createOscillator(freq, 'square');
      const gain = this.createGainNode(this.volume * 0.7);
      
      osc.connect(gain);
      gain.connect(this.audioContext.destination);
      
      const startTime = this.audioContext.currentTime + (index * 0.15);
      
      gain.gain.setValueAtTime(this.volume * 0.7, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.15);
      
      osc.start(startTime);
      osc.stop(startTime + 0.15);
    });
  }

  // Play game win sound
  async playGameWin() {
    if (this.isMuted) return;
    
    // Ensure audio is ready
    const ready = await this.ensureAudioReady();
    if (!ready) return;

    // Ascending victory melody
    const frequencies = [523.25, 659.25, 784.00, 1046.50, 1318.51]; // C5, E5, G5, C6, E6
    
    frequencies.forEach((freq, index) => {
      const osc = this.createOscillator(freq, 'square');
      const gain = this.createGainNode(this.volume * 0.8);
      
      osc.connect(gain);
      gain.connect(this.audioContext.destination);
      
      const startTime = this.audioContext.currentTime + (index * 0.1);
      
      gain.gain.setValueAtTime(this.volume * 0.8, startTime);
      gain.gain.exponentialRampToValueAtTime(0.01, startTime + 0.1);
      
      osc.start(startTime);
      osc.stop(startTime + 0.1);
    });
  }

  // Set volume (0.0 to 1.0)
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.bgMusicGain) {
      this.bgMusicGain.gain.value = this.volume * 0.4;
    }
  }

  // Toggle mute
  toggleMute() {
    this.isMuted = !this.isMuted;
    if (this.isMuted) {
      this.stopBackgroundMusic();
    } else if (this.isPlaying) {
      this.playBackgroundMusic();
    }
    return this.isMuted;
  }

  // Cleanup
  cleanup() {
    this.stopBackgroundMusic();
    // Don't close the audio context - it can't be reused once closed
    // Just stop the music, but keep the context alive
    // if (this.audioContext && this.audioContext.state !== 'closed') {
    //   this.audioContext.close();
    // }
  }
}

// Export singleton instance
export default new PacmanAudio();

